import React, { useState } from "react"
import { Container, Card, CardBody, Form, FormGroup, Label, Input, Button, Alert } from "reactstrap"
import { withTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { createManualOrder } from "../../api"
import { useNavigate } from "react-router-dom"

function CreateExchangeOrderPage({ t }) {
  document.title = t("Create Exchange Order")

  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    exchange: "",
    orderType: "buy",
    cryptoCurrencyCode: "",
    cryptoAmount: "",
    cryptoRate: "",
    fiatAmount: "",
    profit: "",
    txId: "",
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)

  const exchanges = ["Whitebit"]
  const cryptocurrencies = ["USDT"]

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      }

      // Auto-calculate profit and rate when crypto amount or fiat amount changes
      if (name === 'cryptoAmount' || name === 'fiatAmount' || name === 'orderType') {
        const cryptoAmt = parseFloat(name === 'cryptoAmount' ? value : prev.cryptoAmount) || 0
        const fiatAmt = parseFloat(name === 'fiatAmount' ? value : prev.fiatAmount) || 0
        const orderType = name === 'orderType' ? value : prev.orderType

        if (cryptoAmt > 0 && fiatAmt > 0) {
          let profit = 0
          let rate = 0

          if (orderType === 'buy') {
            // BUY: profit = fiat - crypto, rate% = profit / fiat * 100
            profit = fiatAmt - cryptoAmt
            rate = (profit / fiatAmt) * 100
          } else {
            // SELL: profit = crypto - fiat, rate% = profit / crypto * 100
            profit = cryptoAmt - fiatAmt
            rate = (profit / cryptoAmt) * 100
          }

          updated.profit = profit.toFixed(2)
          updated.cryptoRate = rate.toFixed(2)
        }
      }

      return updated
    })
    setError("")
    setSuccess(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    setSuccess(false)

    // Validation
    if (!formData.exchange || !formData.cryptoCurrencyCode ||
        !formData.cryptoAmount || !formData.fiatAmount) {
      setError(t("Please fill in all required fields"))
      setLoading(false)
      return
    }

    try {
      const orderData = {
        exchange: formData.exchange,
        orderType: formData.orderType, // send as lowercase: "buy" or "sell"
        cryptoCurrencyCode: formData.cryptoCurrencyCode,
        cryptoAmount: parseFloat(formData.cryptoAmount),
        cryptoRate: parseFloat(formData.cryptoRate),
        fiatAmount: parseFloat(formData.fiatAmount),
        profit: parseFloat(formData.profit),
        txId: formData.txId || null,
      }

      await createManualOrder(orderData)
      setSuccess(true)

      // Reset form
      setFormData({
        exchange: "",
        orderType: "buy",
        cryptoCurrencyCode: "",
        cryptoAmount: "",
        cryptoRate: "",
        fiatAmount: "",
        profit: "",
        txId: "",
      })

      // Redirect to orders page after 2 seconds
      setTimeout(() => {
        navigate("/orders")
      }, 2000)
    } catch (err) {
      setError(t("Failed to create order") + ": " + err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <h2>{t("Create Exchange Order")}</h2>

          <Card>
            <CardBody>
              {error && <Alert color="danger">{error}</Alert>}
              {success && <Alert color="success">{t("Order created successfully! Redirecting...")}</Alert>}

              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label for="exchange">{t("Exchange")} *</Label>
                  <Input
                    type="select"
                    name="exchange"
                    id="exchange"
                    value={formData.exchange}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t("Select Exchange")}</option>
                    {exchanges.map(exchange => (
                      <option key={exchange} value={exchange}>{exchange}</option>
                    ))}
                  </Input>
                </FormGroup>

                <FormGroup>
                  <Label for="orderType">{t("Order Type")} *</Label>
                  <Input
                    type="select"
                    name="orderType"
                    id="orderType"
                    value={formData.orderType}
                    onChange={handleChange}
                    required
                  >
                    <option value="buy">{t("Buy")}</option>
                    <option value="sell">{t("Sell")}</option>
                  </Input>
                </FormGroup>

                <FormGroup>
                  <Label for="cryptoCurrencyCode">{t("Cryptocurrency")} *</Label>
                  <Input
                    type="select"
                    name="cryptoCurrencyCode"
                    id="cryptoCurrencyCode"
                    value={formData.cryptoCurrencyCode}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t("Select Cryptocurrency")}</option>
                    {cryptocurrencies.map(crypto => (
                      <option key={crypto} value={crypto}>{crypto}</option>
                    ))}
                  </Input>
                </FormGroup>

                <FormGroup>
                  <Label for="cryptoAmount">{t("Crypto Amount")} *</Label>
                  <Input
                    type="number"
                    step="any"
                    name="cryptoAmount"
                    id="cryptoAmount"
                    value={formData.cryptoAmount}
                    onChange={handleChange}
                    placeholder="0.00"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="cryptoRate">{t("Crypto Rate")} (%) *</Label>
                  <Input
                    type="number"
                    step="any"
                    name="cryptoRate"
                    id="cryptoRate"
                    value={formData.cryptoRate}
                    placeholder="0.00"
                    readOnly
                    disabled
                  />
                  <small className="form-text text-muted">
                    {t("Calculated automatically")}
                  </small>
                </FormGroup>

                <FormGroup>
                  <Label for="fiatAmount">{t("Fiat Amount")} (USD) *</Label>
                  <Input
                    type="number"
                    step="any"
                    name="fiatAmount"
                    id="fiatAmount"
                    value={formData.fiatAmount}
                    onChange={handleChange}
                    placeholder="0.00"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="profit">{t("Profit")} *</Label>
                  <Input
                    type="number"
                    step="any"
                    name="profit"
                    id="profit"
                    value={formData.profit}
                    placeholder="0.00"
                    readOnly
                    disabled
                  />
                  <small className="form-text text-muted">
                    {t("Calculated automatically")}
                  </small>
                </FormGroup>

                <FormGroup>
                  <Label for="txId">{t("Transaction Hash (Txid)")}</Label>
                  <Input
                    type="text"
                    name="txId"
                    id="txId"
                    value={formData.txId}
                    onChange={handleChange}
                    placeholder={t("Optional")}
                  />
                  <small className="form-text text-muted">
                    {t("Can be added now or later when finishing the order")}
                  </small>
                </FormGroup>

                <div className="d-flex gap-2">
                  <Button color="primary" type="submit" disabled={loading}>
                    {loading ? t("Creating...") : t("Create Order")}
                  </Button>
                  <Button color="secondary" type="button" onClick={() => navigate("/orders")}>
                    {t("Cancel")}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

CreateExchangeOrderPage.propTypes = {
  t: PropTypes.func.isRequired,
}

export default withTranslation()(CreateExchangeOrderPage)
